import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, finalize, switchMap, take, tap, throwError } from 'rxjs';

import { setIsLoading } from './components/app-loading-overlay/app-loading-overlay.component';
import { AuthService } from './services/auth.service';
import { TurnstileService } from './services/turnstile.service';
import { SKIP_LOADING } from './interceptors/http-context-tokens';

const TURNSTILE_PROTECTED_PATTERNS = [
  /\/authorization\/login$/i,
  /\/password\/forgotPassword$/i,
  /\/password\/resetPassword$/i,
  /\/Communication\/SendClientSupportMessage$/i,
  /\/Authorization\/CreateDemoUser$/i,
];

 @Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);


  constructor(
    private authService: AuthService,
    private router: Router,
    private turnstileService: TurnstileService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip loading overlay for unread messages polling
    const skipLoadingEndpoints = [
      /\/communication\/project\/[^/]+\/topic$/,                                       // CreateEmptyTopic
      /\/communication\/project\/[^/]+\/unresolved-topics\/user$/,                     // GetUnresolvedTopics
      /\/communication\/project\/[^/]+\/topic\/[^/]+\/status$/,                        // UpdateTopicStatus
      /\/communication\/project\/[^/]+\/topic\/[^/]+\/message\/[^/]+$/,                // UpdateTopicMessage
      /\/communication\/project\/[^/]+\/status$/,                                      // UpdateProjectStatus
      /\/communication\/project\/[^/]+\/topic\/[^/]+$/,                                // UpdateTopicTitle
      /\/communication\/project\/[^/]+\/topic\/[^/]+\/message$/,                       // AddTopicMessage
      /\/communication\/projects$/,                                                    // GetMemberProjectCommunications
      /\/Users\/GetUsersByClientId$/,                                                  // GetUsersByClientId
      /\/communication\/topic\/unresolved-topics\/user\/unread-messages$/,             // GetUnreadMessages
      /\/communication\/project\/[^/]+\/topic\/[^/]+\/user\/visited$/,                  // MarkTopicMessagesAsVisited
      /\/communication\/project\/[^/]+\/topics$/,                                      // GetTopicsByProjectId
      /\/files\/image$/,                                                              // UploadImage
      /\/files\/file$/,                                                              // UploadFile
      /\/authorization\/RefreshToken$/,                                               // RefreshToken

      /\/commercialoffer\/bom\/[^/]+\/[^/]+$/,                                       // GetOfferById

      /\/BgGoogle\/googledrive\/file\/[^/]+\/download$/,
      /\/BgGoogle\/googledrive\/folder\/[^/]+\/files$/,    // GetGoogleDriveFilesByFolderId
    ];

    const shouldSkipLoading = skipLoadingEndpoints.some((regex) => regex.test(request.url)) || request.context.get(SKIP_LOADING) === true;

    if (!shouldSkipLoading) {
      setIsLoading(true);
    }

    const isTurnstileProtected = TURNSTILE_PROTECTED_PATTERNS.some(re => re.test(request.url));
    if (isTurnstileProtected) {
      const turnstileToken = this.turnstileService.getToken();
      if (turnstileToken) {
        request = request.clone({
          headers: request.headers.set('cf-turnstile-response', turnstileToken),
        });
      }
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (isTurnstileProtected && event instanceof HttpResponse) {
          // If the backend returned a logical failure (isSuccess: false),
          // the turnstile token has already been consumed and the user will
          // retry — reset the widget so a fresh token is issued.
          const body: any = event.body;
          if (body && typeof body === 'object' && body.isSuccess === false) {
            this.turnstileService.requestReset();
          } else {
            //clear turnstile token
            this.turnstileService.clearToken();
          }
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && !isTurnstileProtected) {
          return this.handle401Error(request, next);
        }

        if (
          isTurnstileProtected &&
          error instanceof HttpErrorResponse &&
          error.status >= 400 &&
          error.status < 500
        ) {
          // Token is single-use; on any 4xx for a turnstile-protected request
          // clear the cached token and ask the widget to issue a fresh one.
          this.turnstileService.requestReset();
        }

        return throwError(() => error);
      }),
      finalize(() => {
        if (!shouldSkipLoading) {
          setIsLoading(false);
        }
      })
    );
  }

	private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return this.authService.refreshToken().pipe(
				switchMap((token) => {
					this.isRefreshing = false;
					this.refreshTokenSubject.next(token);

					// Clone the request with new token
					const authToken = this.authService.getAuthBearerToken();
					const authReq = request.clone({
						headers: request.headers.set('Authorization', `Bearer ${authToken}`)
					});

					return next.handle(authReq);
				}),
				catchError((error) => {
					this.isRefreshing = false;
					this.authService.logout();
					return throwError(() => error);
				})
			);
		} else {
			return this.refreshTokenSubject.pipe(
				filter(token => token != null),
				take(1),
				switchMap(() => {
					const authToken = this.authService.getAuthBearerToken();
					const authReq = request.clone({
						headers: request.headers.set('Authorization', `Bearer ${authToken}`)
					});
					return next.handle(authReq);
				})
			);
		}
	}
}
